import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children, tenantSlug }) {
  // Optimistic hydration from localStorage — dashboard shell paints instantly,
  // /me refresh continues in the background to reconcile.
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("edu360.user");
      return cached ? JSON.parse(cached) : null;
    } catch (_) { return null; }
  });
  const [tenant, setTenant] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`edu360.tenant.${tenantSlug || ""}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.ts < 10 * 60 * 1000) return parsed.data;
      }
    } catch (_) {}
    return null;
  });
  const [loading, setLoading] = useState(() => {
    const token = localStorage.getItem("edu360.token");
    // If we have both a cached user and cached tenant, don't show a blocking spinner.
    if (!token) return false;
    try {
      return !(localStorage.getItem("edu360.user") && sessionStorage.getItem(`edu360.tenant.${tenantSlug || ""}`));
    } catch (_) { return true; }
  });

  useEffect(() => {
    const token = localStorage.getItem("edu360.token");
    if (!token) { setLoading(false); return; }
    let alive = true;
    api
      .me()
      .then(({ data }) => {
        if (!alive) return;
        const t = data.tenant;
        if (tenantSlug && t?.slug !== tenantSlug) {
          localStorage.removeItem("edu360.token");
          localStorage.removeItem("edu360.user");
          setUser(null);
          setTenant(null);
        } else {
          setUser(data.user);
          setTenant(t);
          try {
            localStorage.setItem("edu360.user", JSON.stringify(data.user));
            sessionStorage.setItem(`edu360.tenant.${tenantSlug || ""}`, JSON.stringify({ ts: Date.now(), data: t }));
          } catch (_) {}
        }
      })
      .catch(() => {
        localStorage.removeItem("edu360.token");
        localStorage.removeItem("edu360.user");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantSlug]);

  const login = async (email, password) => {
    const { data } = await api.login({
      tenant_slug: tenantSlug,
      email,
      password,
    });
    localStorage.setItem("edu360.token", data.token);
    localStorage.setItem("edu360.tenant_slug", tenantSlug);
    localStorage.setItem("edu360.user", JSON.stringify(data.user));
    setUser(data.user);
    setTenant(data.tenant);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("edu360.token");
    localStorage.removeItem("edu360.user");
    setUser(null);
    setTenant(null);
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
